import React, { useState } from 'react'
import { BookOpen, Calendar, Clock, Play, Eye, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Badge } from '../../components/ui/badge'
import ErrorDisplay from '../../components/common/ErrorDisplay'
import { useExams } from '../../hooks/useExams'
import { useNavigate } from 'react-router-dom'

const ExamsPage = () => {
  const navigate = useNavigate()
  const [filters, setFilters] = useState({
    status: 'all'
  })

  const { 
    exams, 
    loading, 
    error
  } = useExams({ scope: 'student' })

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleStartExam = (examId) => {
    navigate(`/dashboard/exams/take/${examId}`)
  }

  const handleViewResults = (examId) => {
    navigate(`/dashboard/exams/results/${examId}`)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'warning'
      case 'ongoing': return 'success'
      case 'completed': return 'secondary'
      default: return 'secondary'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <Calendar className="w-4 h-4" />
      case 'ongoing': return <Play className="w-4 h-4" />
      case 'completed': return <CheckCircle className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No date'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    } catch (error) {
      return 'Invalid date'
    }
  }

  // Filter exams: exclude CBT exams and past exams
  const filteredExams = exams.filter(exam => {
    // Exclude CBT exams completely
    if (exam.cbtEnabled) {
      return false;
    }
    
    // Filter by status
    const matchesStatus = filters.status === 'all' || exam.status === filters.status;
    if (!matchesStatus) {
      return false;
    }
    
    // For "all" or "upcoming" filters, exclude past exams
    if (filters.status === 'all' || filters.status === 'scheduled' || filters.status === 'ongoing') {
      const now = new Date();
      
      // If exam has endTime, check if it has passed
      if (exam.examDate && exam.endTime) {
        const examDate = new Date(exam.examDate);
        const [endHour, endMinute] = exam.endTime.split(':');
        const examEndDateTime = new Date(examDate);
        examEndDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
        
        // Only include if exam hasn't ended yet OR status is ongoing
        if (now > examEndDateTime && exam.status !== 'ongoing') {
          return false;
        }
      } else if (exam.examDate) {
        // If no endTime, check examDate
        const examDate = new Date(exam.examDate);
        examDate.setHours(23, 59, 59, 999); // End of day
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Include if exam date is today or in the future, or status is ongoing
        if (examDate < today && exam.status !== 'ongoing') {
          return false;
        }
      }
    }
    
    return true;
  })

  // Calculate stats excluding CBT exams and past exams
  const now = new Date();
  const scheduledExams = exams.filter(e => {
    if (e.cbtEnabled) return false;
    if (e.status !== 'scheduled') return false;
    // Check if exam hasn't passed
    if (e.examDate && e.endTime) {
      const examDate = new Date(e.examDate);
      const [endHour, endMinute] = e.endTime.split(':');
      const examEndDateTime = new Date(examDate);
      examEndDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
      return now <= examEndDateTime;
    }
    if (e.examDate) {
      const examDate = new Date(e.examDate);
      examDate.setHours(23, 59, 59, 999);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return examDate >= today;
    }
    return true;
  });
  
  const activeExams = exams.filter(e => {
    if (e.cbtEnabled) return false;
    if (e.status !== 'ongoing') return false;
    // Check if exam hasn't passed
    if (e.examDate && e.endTime) {
      const examDate = new Date(e.examDate);
      const [endHour, endMinute] = e.endTime.split(':');
      const examEndDateTime = new Date(examDate);
      examEndDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
      return now <= examEndDateTime;
    }
    return true;
  });
  
  const completedExams = exams.filter(e => {
    if (e.cbtEnabled) return false;
    return e.status === 'completed' || e.status === 'graded';
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Exams</h1>
          <p className="text-gray-600 mt-1">View and take your scheduled examinations</p>
        </div>
      </div>

      {/* Error Display */}
      {error && <ErrorDisplay message={error} />}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Scheduled</p>
                <p className="text-2xl font-bold text-gray-900">{scheduledExams.length}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{activeExams.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Play className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{completedExams.length}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Filter by status:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Exams</option>
              <option value="scheduled">Scheduled</option>
              <option value="ongoing">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Exams List */}
      {filteredExams.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No exams found</h3>
            <p className="text-gray-600 text-center">
              {filters.status === 'all' 
                ? 'You don\'t have any exams scheduled at the moment.'
                : `You don't have any ${filters.status} exams.`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredExams.map((exam) => (
            <Card key={exam.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{exam.title}</h3>
                      <p className="text-sm text-gray-600">{exam.subject?.name} - {exam.class?.name}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">{formatDate(exam.examDate || exam.date)} at {exam.startTime}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Duration: {formatDuration(exam.duration)}</span>
                        </div>
                        <span className="text-sm text-gray-500">Marks: {exam.totalMarks}</span>
                      </div>
                      {exam.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{exam.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge variant={getStatusColor(exam.status)} className="flex items-center gap-1">
                      {getStatusIcon(exam.status)}
                      {exam.status}
                    </Badge>
                    <div className="flex space-x-2">
                      {exam.status === 'ongoing' && (
                        <Button
                          onClick={() => handleStartExam(exam.id)}
                          className="flex items-center gap-2"
                        >
                          <Play className="w-4 h-4" />
                          Start Exam
                        </Button>
                      )}
                      {exam.status === 'completed' && (
                        <Button
                          variant="outline"
                          onClick={() => handleViewResults(exam.id)}
                          className="flex items-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          View Results
                        </Button>
                      )}
                      {exam.status === 'scheduled' && (
                        <Button
                          variant="outline"
                          disabled
                          className="flex items-center gap-2"
                        >
                          <Calendar className="w-4 h-4" />
                          Not Available Yet
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

export default ExamsPage
